import { useContext, useEffect, useRef, useState } from 'react';

import { useParams } from 'react-router-dom';

import ContentEditor from './content-editor/ContentEditor';
import ContentApi, { NewContentData } from '../../api/contentApi';
import SectionApi from '../../api/sectionApi';
import SkillApi from '../../api/skillApi';
import ButtonLink from '../../components/atoms/anchor/ButtonLink';
import IconButton from '../../components/atoms/button/IconButton';
import Card from '../../components/organisms/Card';
import { CreationProgressSidebarContent } from '../../components/organisms/sidebar/CreationProgressSidebarContent';
import { SingleTabSidebar } from '../../components/organisms/sidebar/Sidebar';
import HeaderArea from '../../components/page-layout/HeaderArea';
import MainArea from '../../components/page-layout/MainArea';
import PageRightSidebar from '../../components/page-layout/PageRightSidebar';
import SidebarArea from '../../components/page-layout/SidebarArea';
import ContentService from '../../services/contentService';
import { Breadcrumbs } from '../../types/breadcrumbs';
import { ContentData, ContentSpecificData, ContentType } from '../../types/contentTypes';
import SectionData from '../../types/SectionData';
import SkillData from '../../types/skillData';
import ModalContext from '../../utils/ModalContext';

import './ManageContent.scss';

export default function ManageContent() {
  const [skill, setSkill] = useState<SkillData>();
  const [section, setSection] = useState<SectionData>();
  const [sections, setSections] = useState<SectionData[]>(); // Use this to pass to sidebar
  // undefined value is used as a placeholder. When we press 'Add content', we want to show a content editor although
  // there is no underlying section data. In that case it will be represented as undefined in this list until the user
  // saves the section and creates the section data.
  const [contents, setContents] = useState<(ContentData | undefined)[]>();
  const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumbs>([
    [`#`, 'Loading...'],
    ['#', 'Manage Sections'],
    ['#', 'Loading...'],
  ]);

  const { skillSlug, sectionSlug } = useParams<{ skillSlug: string; sectionSlug: string }>();
  const { showConfirmModal } = useContext(ModalContext);

  const scrollRef = useRef<boolean>(false);

  useEffect(() => {
    async function fetchData() {
      const fetchedSkill = await SkillApi.getSkillBySlug(skillSlug!);
      const fetchedSection = await SectionApi.getSectionBySlug(sectionSlug!);
      const fetchedContents = await ContentApi.getContentsBySectionId(fetchedSection.id);
      const fetchedSections = await SectionApi.getSectionsBySkillId(fetchedSkill.id);
      setSkill(fetchedSkill);
      setSection(fetchedSection);
      setContents(fetchedContents.length > 0 ? fetchedContents : [undefined]);
      setSections(fetchedSections);
      setBreadcrumbs([
        ['/', 'Home'],
        [`/skills/${fetchedSkill.slug}`, fetchedSkill.name],
        [`/skills/${fetchedSkill.slug}/sections/edit`, 'Manage sections'],
        ['#', fetchedSection.name],
      ]);
    }

    fetchData();
  }, [skillSlug, sectionSlug]);

  function handleAdd() {
    if (!contents) return;
    scrollRef.current = true;
    setContents([...contents, undefined]);
  }

  function handleNewTypeSelect(contentIndex: number) {
    const newContents = contents?.map((content, currentIndex) => {
      if (contentIndex === currentIndex) {
        return undefined;
      } else {
        return content;
      }
    });
    setContents(newContents);
  }

  async function handleNewContentSubmit(
    props: ContentSpecificData & { headline: string },
    type: ContentType,
    i: number
  ) {
    if (!skill || !section || !contents) return;

    const newContent: NewContentData = {
      ...props,
      skillId: skill.id,
      sectionId: section.id,
      order: i + 1,
      type: type,
    };
    const id = await ContentApi.addContent(newContent);
    const newContents: (ContentData | undefined)[] = contents.map((content, contentIndex) => {
      if (i === contentIndex) {
        // @ts-ignore TODO: Fix TS error, no idea why it's complaining
        const createdContent: ContentData = {
          ...newContent,
          id,
        };
        return createdContent;
      } else {
        return content;
      }
    });
    setContents(newContents);
  }

  async function handleExistingContentSubmit(updatedContent: ContentData, i: number) {
    if (!skill || !section || !contents) return;
    await ContentApi.setContent(updatedContent);
    const newContents: (ContentData | undefined)[] = contents.map((content, contentIndex) => {
      if (i === contentIndex) {
        return updatedContent;
      } else {
        return content;
      }
    });
    setContents(newContents);
  }

  async function handleDelete(content: ContentData | NewContentData | undefined, index: number) {
    showConfirmModal(async () => {
      if (content && 'id' in content) {
        await ContentApi.removeContent(content);
      }
      const newContents = contents?.filter((content, currentIndex) => currentIndex !== index);
      setContents(newContents);
    }, `Are you sure you want to delete content${content?.headline ? ` '${content.headline}'` : ''}?`);
  }

  async function handleSwapOrder(contents: SectionData[] | ContentData[], index1: number, index2: number) {
    const newContents = await ContentService.swapContentsInArray(index1, index2, contents as ContentData[]);
    setContents(newContents);
  }

  const ContentEditors =
    section &&
    skill &&
    contents?.map((content, i) => {
      return (
        <ContentEditor
          key={content && 'id' in content ? content.id : i}
          className="mb-32"
          content={content}
          onNewContentSubmit={(props, tpe) => handleNewContentSubmit(props, tpe, i)}
          onExistingContentSubmit={content => handleExistingContentSubmit(content, i)}
          onNewTypeSelect={() => handleNewTypeSelect(i)}
          onDelete={() => handleDelete(content, i)}
          scroll={scrollRef.current}
          id={i.toString()}
        />
      );
    });

  return (
    <PageRightSidebar activeNavlink="my-creations" breadcrumbs={breadcrumbs}>
      <HeaderArea>
        <h1 className="strong-heading">{section?.name}</h1>
      </HeaderArea>
      <MainArea>
        <Card className="p-16">
          {!!ContentEditors?.length && <div className="mb-16">{ContentEditors}</div>}
          {contents && (
            <>
              <div className="flex">
                <IconButton className="mr-auto" icon="add" label="Add content" onClick={handleAdd} />
                <ButtonLink className="ml-auto" visualType="secondary" to={`/skills/${skillSlug}/sections/edit`}>
                  Back to section
                </ButtonLink>
              </div>
            </>
          )}
        </Card>
      </MainArea>
      <SidebarArea>
        <SingleTabSidebar
          tabLabel={'Your progress'}
          component={
            skill && (
              <CreationProgressSidebarContent
                skill={skill}
                sections={sections}
                contents={contents}
                handleSwapOrder={handleSwapOrder}
              />
            )
          }
        />
      </SidebarArea>
    </PageRightSidebar>
  );
}
